import React from 'react'
import type { Maybe } from '@adc/sdk/types'
import classnames from 'classnames/bind'
import { useMountEffect } from '../../hooks'
import {
  Button,
  CardContainer,
  FixedMessage,
  Text,
  NullComponent,
} from '../elementals'
import styles from './styles.scss'

const cx = classnames.bind(styles)

type VerificationInitializerProps = {
  showIntro?: boolean
  error?: Maybe<string>
  onGoBack?: () => void
  initiateIdentityVerification: () => void
}

const VerificationInitializer = ({
  showIntro,
  error,
  onGoBack,
  initiateIdentityVerification,
}: VerificationInitializerProps) => {
  useMountEffect(() => {
    if (showIntro) return
    initiateIdentityVerification()
  })

  if (!showIntro) return <NullComponent />

  return (
    <CardContainer
      dropShadow='small'
      borderRadius='extra-small'
      padding='extra-small'
    >
      <Text.Header className='u-tc' color='gray' level={2}>
        First, Let’s Verify Your ID
      </Text.Header>
      <FixedMessage
        isVisible={Boolean(error)}
        elmId='identity_verification_error_message'
        role='error'
        className='u-mt-6'
        showIcon
      >
        {error}
      </FixedMessage>
      <Text>
        We verify your identity, just one time, to make the buying process
        smoother for you. Please note that your identity can only be used with
        one account.
      </Text>
      <Text data-elm-id='what_you_need_section' className='u-mt-4'>
        <strong>Have either of these ready:</strong>
        <ul>
          <li>Driver's License</li>
          <li>Identity Card</li>
          <li>Passport</li>
        </ul>
      </Text>
      <Text data-elm-id='legal_section' className='u-mt-2 u-mb-2'>
        You will be redirected to our third-party partner Jumio to complete the
        verification process.
      </Text>
      <div className='status-button-container'>
        <span className={cx('u-mr-1')}>
          {onGoBack && (
            <Button
              role='secondary'
              onClick={onGoBack}
              elmId='identity_verification_page_intro_back_button'
            >
              Back
            </Button>
          )}
        </span>
        <Button
          role='primary'
          onClick={initiateIdentityVerification}
          elmId='identity_verification_page_intro_begin_button'
        >
          Start Now
        </Button>
      </div>
    </CardContainer>
  )
}

export default VerificationInitializer
